import { useEffect } from "react";

interface FAQItem {
  question: string;
  answer: string;
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  type?: "website" | "article";
  datePublished?: string;
  dateModified?: string;
  faqs?: FAQItem[];
  breadcrumbs?: BreadcrumbItem[];
}

export default function SEOHead({
  title,
  description,
  canonical,
  type = "website",
  datePublished,
  dateModified,
  faqs,
  breadcrumbs,
}: SEOHeadProps) {
  useEffect(() => {
    // ── Title ──────────────────────────────────────────────────────────
    document.title = title;

    // ── Meta Description ──────────────────────────────────────────────
    let metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", description);

    // ── Canonical ─────────────────────────────────────────────────────
    let canonicalEl = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) {
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.setAttribute("href", canonical);
    }

    // ── Open Graph ────────────────────────────────────────────────────
    const setMeta = (attr: "property" | "name", key: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", type);
    if (canonical) setMeta("property", "og:url", canonical);

    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);

    // ── JSON-LD Article Schema ─────────────────────────────────────────
    const existingScript = document.querySelector('script[data-seohead="article"]');
    if (existingScript) existingScript.remove();

    if (type === "article") {
      const script = document.createElement("script");
      script.setAttribute("type", "application/ld+json");
      script.setAttribute("data-seohead", "article");
      const schema: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": title,
        "description": description,
        "inLanguage": "es",
        "author": {
          "@type": "Person",
          "@id": "https://segurosr22usa.com/#founder",
          "name": "Estiven Gabriel Aguiluz Suazo",
          "jobTitle": "Fundador y Editor",
          "url": "https://segurosr22usa.com/sobre-nosotros",
        },
        "publisher": {
          "@type": "Organization",
          "@id": "https://segurosr22usa.com/#organization",
          "name": "SeguroSR22USA.com",
          "url": "https://segurosr22usa.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://segurosr22usa.com/favicon.png",
          },
        },
      };
      if (canonical) schema["url"] = canonical;
      if (datePublished) schema["datePublished"] = datePublished;
      if (dateModified) schema["dateModified"] = dateModified;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    // ── FAQPage JSON-LD ──────────────────────────────────────────────
    const existingFaq = document.querySelector('script[data-seohead="faq"]');
    if (existingFaq) existingFaq.remove();

    if (faqs && faqs.length > 0) {
      const faqScript = document.createElement("script");
      faqScript.setAttribute("type", "application/ld+json");
      faqScript.setAttribute("data-seohead", "faq");
      faqScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((f) => ({
          "@type": "Question",
          "name": f.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": f.answer,
          },
        })),
      });
      document.head.appendChild(faqScript);
    }

    // ── BreadcrumbList JSON-LD ────────────────────────────────────────
    const existingBc = document.querySelector('script[data-seohead="breadcrumb"]');
    if (existingBc) existingBc.remove();

    if (breadcrumbs && breadcrumbs.length > 0) {
      const bcScript = document.createElement("script");
      bcScript.setAttribute("type", "application/ld+json");
      bcScript.setAttribute("data-seohead", "breadcrumb");
      bcScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((bc, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": bc.name,
          "item": bc.url,
        })),
      });
      document.head.appendChild(bcScript);
    }

    return () => {
      const articleScript = document.querySelector('script[data-seohead="article"]');
      if (articleScript) articleScript.remove();
      const faqScript = document.querySelector('script[data-seohead="faq"]');
      if (faqScript) faqScript.remove();
      const bcScript = document.querySelector('script[data-seohead="breadcrumb"]');
      if (bcScript) bcScript.remove();
    };
  }, [title, description, canonical, type, datePublished, dateModified, faqs, breadcrumbs]);

  return null;
}
